'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

interface Service {
  id: string;
  title: string;
  description: string;
  href: string;
  image: string;
  alt: string;
}

const services: Service[] = [
{
  id: 'container',
  title: 'Container Transport',
  description: 'Full container loads (FCL) door-to-door worldwide.',
  href: '/container-transport',
  image: "/assets/images/remote/photo-1696287146802-af8ccce45601.jpg",
  alt: 'Large container ship loaded with colorful cargo containers at port, aerial view, blue ocean'
},
{
  id: 'lcl',
  title: 'Less Container Load',
  description: 'Shared container space for smaller shipments — cost-efficient LCL.',
  href: '/lcl-transport',
  image: "/assets/images/remote/photo-1620194176905-6faf9bfb0a26.jpg",
  alt: 'Warehouse interior with stacked cargo boxes and pallets, warm industrial lighting'
},
{
  id: 'airfreight',
  title: 'Airfreight Transport',
  description: 'Express and standard air cargo to any destination globally.',
  href: '/airfreight-transport',
  image: "/assets/images/remote/rocket-gen-img-1e5ff4740-1767073191032.png",
  alt: 'Cargo aircraft on tarmac with freight containers being loaded, sunset sky'
},
{
  id: 'inland',
  title: 'Inland Transport',
  description: 'Road and rail freight across Europe and beyond.',
  href: '/inland-transport',
  image: "/assets/images/remote/rocket-gen-img-14fa6dc95-1773104688539.png",
  alt: 'White freight truck driving on a highway through green countryside, clear blue sky'
},
{
  id: 'customs',
  title: 'Customs Representation',
  description: 'Licensed customs brokers handling all import/export declarations.',
  href: '/customs-representation',
  image: "/assets/images/remote/rocket-gen-img-1912e34f9-1764670091202.png",
  alt: 'Customs officer reviewing shipping documents at border checkpoint, professional office setting'
},
{
  id: 'port',
  title: 'Port Operations',
  description: 'Stevedoring, storage, and cargo handling at major ports.',
  href: '/port-operations',
  image: "/assets/images/remote/photo-1707811209642-481d02372503.jpg",
  alt: 'Port crane lifting shipping container at night, bright spotlights, industrial waterfront'
},
{
  id: 'insurance',
  title: 'Cargo Insurance',
  description: 'Comprehensive cover for your goods in transit — all modes.',
  href: '/cargo-insurance',
  image: "/assets/images/remote/photo-1587467762743-fc322e2252ca.jpg",
  alt: 'Secured cargo crates in warehouse with protective wrapping, dim industrial space'
},
{
  id: 'bank',
  title: 'Bank Guarantee',
  description: 'Financial security instruments for customs and trade obligations.',
  href: '/bank-guarantee',
  image: "/assets/images/remote/rocket-gen-img-1eb4405ca-1774453617189.png",
  alt: 'Professional signing financial documents at a desk with pen, banking and trade finance concept'
}];


const ServicesSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const els = sectionRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right');
    if (!els) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="relative py-20 lg:py-28" ref={sectionRef} style={{ background: '#FFFFFF' }}>
      {/* Section header */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
        <div className="reveal flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-body mb-3" style={{ color: '#1B6B8A' }}>
              <span className="w-6 h-px" style={{ background: '#1B6B8A' }} />
              What We Do
            </span>
            <h2 className="font-display text-4xl lg:text-5xl leading-tight" style={{ color: '#0F2340' }}>
              End-to-end freight,<br />
              <span className="italic" style={{ color: '#1B6B8A' }}>every mode.</span>
            </h2>
          </div>
          <p className="font-body text-base max-w-sm leading-relaxed" style={{ color: '#3A5070' }}>
            From container shipping to air cargo and customs clearance — WTO handles your supply chain with precision across all transport modes.
          </p>
        </div>
      </div>

      {/* Uniform 4-col grid — all cards same height */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
          {services.map((service, i) =>
          <a
            key={service.id}
            href={service.href}
            className="service-card reveal rounded-2xl overflow-hidden group relative h-56 lg:h-64"
            style={{ transitionDelay: `${i * 55}ms` }}>
            
              {/* Background image */}
              <AppImage
              src={service.image}
              alt={service.alt}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-700"
              sizes="(max-width: 768px) 50vw, 25vw" />
            

              {/* Dark scrim */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/10" />

              {/* Hover overlay — teal tint */}
              <div
              className="overlay absolute inset-0 opacity-0 group-hover:opacity-100"
              style={{ background: 'rgba(27,107,138,0.15)', transition: 'opacity 0.35s ease' }} />
            

              {/* Content */}
              <div className="absolute inset-0 p-4 lg:p-5 flex flex-col justify-end">
                <p className="text-xs text-white font-body mb-1 line-clamp-1 hidden lg:block font-medium" style={{ textShadow: '0 1px 6px rgba(0,0,0,1)' }}>{service.description}</p>
                <div className="flex items-end justify-between gap-2">
                  <h3 className="font-display text-sm lg:text-base text-white font-bold leading-tight" style={{ textShadow: '0 2px 8px rgba(0,0,0,1), 0 1px 3px rgba(0,0,0,1)' }}>
                    {service.title}
                  </h3>
                  <div className="w-7 h-7 rounded-full border border-white/20 bg-white/5 flex items-center justify-center shrink-0 group-hover:border-teal-light/50 transition-all duration-300">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-white/70 group-hover:text-teal transition-colors duration-300" style={{ transform: 'rotate(-45deg)' }}>
                      <path d="M5 12h14M13 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
                {/* Animated underline */}
                <div className="h-px mt-2 scale-x-0 origin-left group-hover:scale-x-100" style={{ background: 'rgba(27,107,138,0.8)', transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1)' }} />
              </div>
            </a>
          )}
        </div>
      </div>
    </section>);

};

export default ServicesSection;